import React, {useEffect, useMemo, useState} from "react";
import {GlobalLayoutPropsInterface, MMP_FORM} from "../../types";
import {RouteComponentProps} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {RootStore} from "../../configureStore";
import {deleteSignUpCookies, getCookie} from "../../utils/utils";
import {validateField} from "../../utils/validation";
import useBasePage from "../../hooks/useBasePage";
import * as actions from "./actions";
import {usePageDataHook, useTrackingHook} from "../../hooks/utility";
import config from "../../config/config";
import {
    // setAversionLayerHandler,
    setReferrerAndPageType,
} from "../../components/Layout/actions";
import {
    isListingType,
    clearRegistrationAndReferralRedux,
    isShortlistType,
    isRegistrationType,
    isMMPType,
    isReferralSignup,
} from "../../components/CTA/response";
import {InitialParamsType, SignUpCookie} from "./types";
import {isPWA} from "../../utils/url";
import {getPageType, checkToShowSignUpPage, checkToShowLoginPage} from "./utils";
import ScholarshipLoadableLayer from "../../components/CTA/ScholarshipApply/Loadable";
import RegistrationLogin from "../../components/RegistrationLogin";

interface Props extends RouteComponentProps {
    globalLayoutProps: GlobalLayoutPropsInterface;
    universityId?: number;
    courseId?: number;
    trackingId?: number;
    referrerURL?: string;
}

const useFetchInitialParamsHandler = (): InitialParamsType => {
    const ctaTrackingData = useSelector(
        (state: RootStore) => state.RegistrationLoginPage.ctaTrackingData,
    );
    const mmpTrackingData = useSelector(
        (state: RootStore) => state.RegistrationLoginPage.mmpTrackingData,
    );
    const trackingData = mmpTrackingData ?? ctaTrackingData;
    const dispatch = useDispatch();

    useEffect(() => {
        if (!ctaTrackingData && mmpTrackingData) {
            dispatch(actions.setTrackingDataAction(mmpTrackingData, MMP_FORM));
        }
    }, [ctaTrackingData, mmpTrackingData, dispatch]);

    const [result] = useState(() => {
        if (trackingData) {
            isClientEnv && deleteSignUpCookies();
            const {trackingId, referralURL} = trackingData;
            return {
                trackingId,
                universityId:
                    isListingType(trackingData) || isShortlistType(trackingData)
                        ? trackingData.universityId
                        : undefined,
                courseId:
                    isListingType(trackingData) || isShortlistType(trackingData)
                        ? trackingData.courseId
                        : undefined,
                mmpId: isMMPType(trackingData) ? trackingData.mmpId : undefined,
                referralURL,
                showSignUpSection: !isRegistrationType(trackingData),
                referrerDisplayName: isReferralSignup(trackingData)
                    ? trackingData?.referrerDisplayName
                    : undefined,
            };
        } else {
            //It will run on client side only as getCookie will return undefined on server
            const signUpFormParams: string = getCookie("signUpFormParams") as string;
            if (validateField(signUpFormParams)) {
                const signUpFormParamsKey: SignUpCookie = JSON.parse(
                    atob(signUpFormParams),
                ) as SignUpCookie;
                const trackingId = signUpFormParamsKey.tkey;
                if (!trackingId) {
                    deleteSignUpCookies();
                    return {
                        showSignUpSection: false,
                    };
                }
                const courseId = signUpFormParamsKey.cId ?? null;
                const universityId = signUpFormParamsKey.uId ?? null;
                const referralURL = signUpFormParamsKey.rf.replace(
                    config.SHIKSHA_STUDYABROAD_BASENAME,
                    "",
                );
                const isLoginSignUp = signUpFormParamsKey.isSingUpLogin;

                return {
                    universityId,
                    courseId,
                    trackingId,
                    referralURL,
                    showSignUpSection: !isLoginSignUp,
                };
            } else {
                return {
                    showSignUpSection: false,
                };
            }
        }
    });
    return result;
};

const RegistrationLoginPage: React.FC<Props> = (props: Props) => {
    const dispatch = useDispatch();
    const initialParamData = useFetchInitialParamsHandler();

    const {
        trackingId = undefined,
        referralURL,
        universityId = null,
        courseId = null,
        showSignUpSection,
        mmpId,
        referrerDisplayName,
    } = initialParamData ?? {};

    const isMobile = useSelector((state: RootStore) => state.layout.isMobile);

    const apiParameters = useMemo(() => {
        return {
            universityId,
            courseId,
            trackingId,
            isMobile,
            mmpId,
            referrerDisplayName,
        };
    }, [universityId, courseId, trackingId, isMobile, mmpId, referrerDisplayName]);

    const [showContentLoader, setContentLoaderStatus] = useState(false);

    const signUpPageData = useSelector(
        (state: RootStore) => state.RegistrationLoginPage.signUpPageData,
    );

    const loginPageData = useSelector(
        (state: RootStore) => state.RegistrationLoginPage.loginPageData,
    );

    const isUserLoggedIn = useSelector(
        (state: RootStore) => state.layout?.userAndShortlistData?.userLoggedIn ?? false,
    );

    const {location, globalLayoutProps} = props;
    const {activePage, pageStatus} = getPageType(location.pathname);
    const {isLogin, isSignUp} = pageStatus;
    useBasePage(location, globalLayoutProps, activePage);

    const [showLogin, setShowLogin] = useState(() => {
        return checkToShowLoginPage(isUserLoggedIn);
    });

    const [showSingUp /*, setShowSignUp*/] = useState(() => {
        return checkToShowSignUpPage({
            userLoggedIn: isUserLoggedIn,
            showSignUpSection,
        });
    });

    const [showPage] = useState(() => {
        return (isSignUp && showSingUp) || (isLogin && showLogin);
    });

    const pageDataHookObj = {
        reduxBucketName: globalLayoutProps.reduxBucketName,
        pageData: isSignUp ? signUpPageData : loginPageData,
        setContentLoaderStatus,
        location,
        api: actions.fetchInitialData,
        actions,
        startFromTop: true,
        isActive: showPage,
        apiParameters,
    };
    usePageDataHook(pageDataHookObj);

    useTrackingHook({
        pageName: activePage,
        pageData: isSignUp ? signUpPageData : loginPageData,
        isLoaderActive: showContentLoader,
        isActive: showPage,
    });

    useEffect(() => {
        setShowLogin(checkToShowLoginPage());
        // setShowSignUp(checkToShowSignUpPage({showSignUpSection}));
    }, [location]);

    useEffect(() => {
        return () => {
            clearRegistrationAndReferralRedux({dispatch});
        };
    }, [dispatch]);

    useEffect(() => {
        if (!showPage) {
            window.onbeforeunload = null;
            window.location.href = config.SHIKSHA_STUDYABROAD_HOME;
        }
    }, [showPage]);

    useEffect(() => {
        let dispatchObj = {
            referrerURL: config.SHIKSHA_STUDYABROAD_HOME,
            isReferrerPWA: false,
        };
        if (referralURL) {
            dispatchObj = {
                referrerURL: referralURL,
                isReferrerPWA: isPWA(referralURL),
            };
        }
        dispatch(setReferrerAndPageType(dispatchObj));
    }, [dispatch, referralURL]);

    const scholarshipLayerData = useSelector(
        (state: RootStore) => state.RegistrationLoginPage.scholarshipLayerData,
    );

    return useMemo(() => {
        if (scholarshipLayerData?.applyLink) {
            return (
                <ScholarshipLoadableLayer
                    scholarshipLayerData={scholarshipLayerData}
                    isMobile={isMobile}
                />
            );
        }
        return (
            <RegistrationLogin
                signUpPageData={signUpPageData}
                loginPageData={loginPageData}
                showLogin={showLogin}
                showSignUp={showSingUp}
                showContentLoader={showContentLoader}
            />
        );
    }, [
        showLogin,
        showSingUp,
        signUpPageData,
        showContentLoader,
        scholarshipLayerData,
        loginPageData,
        isMobile,
    ]);
};

export default RegistrationLoginPage;
